/**
 * AssignmentModal — asignar jefatura y unidad a un trabajador, con su histórico.
 *
 * ⚠️ Lo que no se cambia **no se manda**: el backend distingue «no toqué esta dimensión»
 * de «déjala vacía», así que cambiar solo el jefe conserva el área. Si este formulario
 * mandara siempre las tres, un cambio de jefatura borraría el área en cada guardado.
 */
import { todayISO } from '../../../lib/utils'
import { useEffect, useState } from 'react'
import { History } from 'lucide-react'

import { Button } from '@/components/Button'
import DatePicker from '@/components/DatePicker'
import { ModalCancelButton } from '@/components/ModalActions'
import { Modal } from '@/components/ds/Modal'
import { EntityAutocomplete } from '@/components/EntityAutocomplete'
import { useToast } from '@/components/Toast'
import { getApiErrorMessage } from '@/lib/apiClient'
import { labelCls } from '@/lib/formStyles'
import { fmtDate } from '@/lib/utils'

import {
  useAreas, useAssignEmployee, useDivisions, useEmployeeAssignment,
  type AssignmentPayload,
} from '../hooks/useOrgChart'
import { useEmployees } from '../hooks/usePayroll'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'

interface Props {
  employee: { id: string; name: string }
  onClose: () => void
}

export function AssignmentModal({ employee, onClose }: Props) {
  const toast = useToast()
  const { data, isLoading } = useEmployeeAssignment(employee.id)
  const { data: empData } = useEmployees({ active: 'true', page_size: 500 })
  const { data: divData } = useDivisions()
  const { data: areaData } = useAreas()
  const assign = useAssignEmployee(employee.id)

  const [supervisor, setSupervisor] = useState<string>('')
  const [area, setArea] = useState<string>('')
  const [division, setDivision] = useState<string>('')
  const [reason, setReason] = useState('')
  const [from, setFrom] = useState('')
  const [verHistorial, setVerHistorial] = useState(false)

  const actual = data?.current
  useEffect(() => {
    setSupervisor(actual?.supervisor_id || '')
    setArea(actual?.area_id || '')
    setDivision(actual?.division_id || '')
  }, [actual])

  const candidatos = (empData?.employees || [])
    .filter((e: { id: string }) => e.id !== employee.id)
    .map((e: { id: string; full_name: string; current_position?: string }) => ({
      id: e.id,
      label: e.current_position ? `${e.full_name} — ${e.current_position}` : e.full_name,
    }))

  const guardar = async () => {
    // Solo las dimensiones que efectivamente cambiaron.
    const payload: AssignmentPayload = {}
    if (supervisor !== (actual?.supervisor_id || '')) payload.supervisor = supervisor || null
    if (area !== (actual?.area_id || '')) payload.area = area || null
    if (division !== (actual?.division_id || '')) payload.division = division || null
    if (!Object.keys(payload).length) {
      toast.warning('No hay cambios que guardar.')
      return
    }
    if (reason.trim()) payload.reason = reason.trim()
    if (from) payload.effective_from = from
    try {
      await assign.mutateAsync(payload)
      toast.success('Asignación registrada.')
      onClose()
    } catch (e) {
      toast.error(getApiErrorMessage(e))
    }
  }

  const historial = (data?.history || []).filter(h => !h.is_current)

  return (
    <Modal
      open
      onClose={onClose}
      title={`Asignación organizacional — ${employee.name}`}
      size="lg"
      actions={
        <>
          <ModalCancelButton onClick={onClose} />
          <Button onClick={guardar} isPending={assign.isPending} pendingLabel="Guardando…">
            Guardar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-content-muted">Cargando…</p>
        ) : (
          <>
            <div>
              <label htmlFor="assignmentmodal-jefe-directo" className={labelCls}>Jefe directo</label>
              <EntityAutocomplete id="assignmentmodal-jefe-directo"
                options={candidatos}
                value={supervisor}
                onChange={v => setSupervisor(v || '')}
                placeholder="— Sin jefatura —"
                allowClear
                clearLabel="Sin jefatura"
              />
              <p className="text-[11px] text-content-subtle mt-1">
                No puede ser alguien que dependa de este trabajador: cerraría un círculo y
                los dos quedarían fuera de la jerarquía.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SelectField
                dense
                label="División"
                value={division}
                onChange={e => setDivision(e.target.value)}
              >
                <option value="">— Sin división —</option>
                {(divData?.divisions || []).filter(d => d.is_active).map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </SelectField>
              <SelectField
                dense
                label="Área"
                value={area}
                onChange={e => setArea(e.target.value)}
              >
                <option value="">— Sin área —</option>
                {(areaData?.areas || [])
                .filter(a => a.is_active && (!division || a.division === division))
                .map(a => (
                <option key={a.id} value={a.id}>
                {a.division_name ? `${a.division_name} / ${a.name}` : a.name}
                </option>
                ))}
              </SelectField>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Vigente desde</label>
                <DatePicker
                  value={from}
                  onChange={setFrom}
                  max={todayISO()}
                  aria-label="Fecha desde la que rige la asignación"
                />
                <p className="text-[11px] text-content-subtle mt-1">
                  Vacío = hoy. Sirve para registrar un cambio que ya ocurrió; no admite
                  fechas futuras.
                </p>
              </div>
              <TextField
                dense
                label="Motivo"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Promoción, reorganización…"
                inputProps={{ maxLength: 200 }}
              />
            </div>

            {historial.length > 0 && (
              <div className="border-t border-divider pt-3">
                <button
                  type="button"
                  onClick={() => setVerHistorial(v => !v)}
                  className="flex items-center gap-1.5 text-xs font-medium text-content-muted hover:text-content"
                >
                  <History size={13} />
                  {verHistorial ? 'Ocultar' : 'Ver'} historial ({historial.length})
                </button>
                {verHistorial && (
                  <ul className="mt-2 space-y-1.5">
                    {historial.map(h => (
                      <li key={h.id} className="text-xs text-content-muted">
                        <span className="text-content">
                          {fmtDate(h.valid_from)} → {h.valid_until ? fmtDate(h.valid_until) : 'hoy'}
                        </span>
                        {' · '}
                        {h.supervisor_name || 'sin jefatura'}
                        {h.area_name ? ` · ${h.area_name}` : ''}
                        {h.reason ? ` · ${h.reason}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
